import React, { useRef, useState, useEffect } from 'react';
import styles from './DrawingCanvas.module.css';

// 笔刷类型定义
const BRUSH_TYPES = {
  normal: { name: '普通', icon: '✎' },
  spray: { name: '喷枪', icon: '✦' },
  star: { name: '星星', icon: '★' },
  dotted: { name: '点线', icon: '···' },
};

export default function DrawingCanvas({ onSave, onClose, archetype, isFullPage = false }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState('#ffffff');
  const [brushSize, setBrushSize] = useState(3);
  const [brushType, setBrushType] = useState('normal');
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [history, setHistory] = useState([]);
  const [isLaunching, setIsLaunching] = useState(false);
  const [message, setMessage] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [isEraser, setIsEraser] = useState(false);
  const [dotCounter, setDotCounter] = useState(0);

  const colors = [
    '#ffffff', '#ff6b6b', '#ffa94d', '#ffd43b', 
    '#69db7c', '#4dabf7', '#9775fa', '#f783ac'
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    // 更亮的背景色，增加对比度
    ctx.fillStyle = '#12121f';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // 添加微弱的网格线增加层次感
    ctx.strokeStyle = 'rgba(100, 80, 150, 0.08)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= canvas.width; i += 40) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, canvas.height);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(canvas.width, i);
      ctx.stroke();
    }
    
    saveToHistory();
  }, []);

  const saveToHistory = () => {
    const canvas = canvasRef.current;
    const imageData = canvas.toDataURL();
    setHistory(prev => [...prev, imageData]);
  };

  const undo = () => {
    if (history.length <= 1) return;
    const newHistory = [...history];
    newHistory.pop();
    const previousState = newHistory[newHistory.length - 1];
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = previousState;
    setHistory(newHistory);
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    setLastPos(getPos(e));
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    
    if (isEraser) {
      // 橡皮擦模式 - 用背景色绘制
      ctx.beginPath();
      ctx.moveTo(lastPos.x, lastPos.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.strokeStyle = '#12121f';
      ctx.lineWidth = brushSize * 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    } else {
      // 根据笔刷类型绘制
      switch (brushType) {
        case 'spray':
          drawSpray(ctx, pos);
          break;
        case 'star':
          drawStar(ctx, pos);
          break;
        case 'dotted':
          drawDotted(ctx, pos);
          break;
        default:
          drawNormal(ctx, pos);
      }
    }
    setLastPos(pos);
  };

  // 普通笔刷
  const drawNormal = (ctx, pos) => {
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = color;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  // 喷枪效果
  const drawSpray = (ctx, pos) => {
    const density = brushSize * 3;
    for (let i = 0; i < density; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * brushSize * 2;
      const x = pos.x + Math.cos(angle) * radius;
      const y = pos.y + Math.sin(angle) * radius;
      const size = Math.random() * 1.5 + 0.5;
      
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.globalAlpha = Math.random() * 0.5 + 0.3;
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  };

  // 星星笔刷
  const drawStar = (ctx, pos) => {
    const distance = Math.sqrt((pos.x - lastPos.x) ** 2 + (pos.y - lastPos.y) ** 2);
    if (distance < brushSize * 2) return;
    
    const size = brushSize * 1.5;
    const spikes = 4;
    const outerRadius = size;
    const innerRadius = size / 2;
    
    ctx.save();
    ctx.translate(pos.x, pos.y);
    ctx.rotate(Math.random() * Math.PI);
    ctx.beginPath();
    
    for (let i = 0; i < spikes * 2; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = (i * Math.PI) / spikes;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.shadowColor = color;
    ctx.shadowBlur = 8;
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.restore();
  };

  // 点线笔刷
  const drawDotted = (ctx, pos) => {
    setDotCounter(prev => {
      const newCount = prev + 1;
      if (newCount % 3 === 0) {
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, brushSize, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
      }
      return newCount;
    });
  };

  const stopDrawing = () => {
    if (isDrawing) saveToHistory();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#12121f';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // 重新绘制网格
    ctx.strokeStyle = 'rgba(100, 80, 150, 0.08)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= canvas.width; i += 40) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, canvas.height);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(canvas.width, i);
      ctx.stroke();
    }
    
    saveToHistory();
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    const imageData = canvas.toDataURL('image/png');
    setImagePreview(imageData);
    setIsLaunching(true);

    // 生成坐标
    const ra = `${Math.floor(Math.random() * 24)}h ${Math.floor(Math.random() * 60)}m`;
    const dec = `${Math.random() > 0.5 ? '+' : '-'}${Math.floor(Math.random() * 90)}° ${Math.floor(Math.random() * 60)}'`;

    // 获取现有黑匣子位置，找一个不重叠的位置
    const saved = localStorage.getItem('gallery_paintings');
    const existingPaintings = saved ? JSON.parse(saved) : [];
    
    // 生成不重叠的位置
    const minDistance = 15;
    let bestPos = { x: 8 + Math.random() * 84, y: 8 + Math.random() * 76 };
    let bestMinDist = 0;
    
    for (let attempt = 0; attempt < 50; attempt++) {
      const x = 8 + Math.random() * 84;
      const y = 8 + Math.random() * 76;
      
      let minDist = Infinity;
      for (const p of existingPaintings) {
        if (p.posX && p.posY) {
          const dist = Math.sqrt((x - p.posX) ** 2 + (y - p.posY) ** 2);
          minDist = Math.min(minDist, dist);
        }
      }
      
      if (existingPaintings.length === 0 || minDist >= minDistance) {
        bestPos = { x, y };
        break;
      }
      
      if (minDist > bestMinDist) {
        bestMinDist = minDist;
        bestPos = { x, y };
      }
    }

    const painting = {
      id: Date.now(),
      image: imageData,
      archetype: archetype,
      date: new Date().toLocaleDateString('zh-CN'),
      message: message.trim() || null,
      coordinate: { ra, dec },
      posX: bestPos.x,
      posY: bestPos.y,
      glowIntensity: 0.5 + Math.random() * 0.5,
      scale: 0.9 + Math.random() * 0.4,
      echoCount: 0,
    };

    existingPaintings.unshift(painting);
    localStorage.setItem('gallery_paintings', JSON.stringify(existingPaintings));

    setTimeout(() => {
      onSave && onSave(painting);
    }, 5000);
  };

  return (
    <div className={`${styles.overlay} ${isFullPage ? styles.fullPage : ''}`}>
      <div className={`${styles.container} ${isLaunching ? styles.launching : ''}`}>
        <div className={styles.canvasArea}>
          <div className={styles.canvasWrapper}>
            <canvas
              ref={canvasRef}
              width={400}
              height={400}
              className={styles.canvas}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
          
          <div className={styles.toolsBar}>
            <div className={styles.colors}>
              {colors.map(c => (
                <button
                  key={c}
                  className={`${styles.colorBtn} ${color === c && !isEraser ? styles.activeColor : ''}`}
                  style={{ background: c, color: c }}
                  onClick={() => { setColor(c); setIsEraser(false); }}
                />
              ))}
            </div>
            <div className={styles.divider} />
            <div className={styles.brushTypes}>
              {Object.entries(BRUSH_TYPES).map(([type, { name, icon }]) => (
                <button
                  key={type}
                  className={`${styles.brushTypeBtn} ${brushType === type && !isEraser ? styles.activeBrush : ''}`}
                  onClick={() => { setBrushType(type); setIsEraser(false); }}
                  title={name}
                >
                  <span className={styles.brushIcon}>{icon}</span>
                </button>
              ))}
            </div>
            <div className={styles.divider} />
            <button
              className={`${styles.eraserBtn} ${isEraser ? styles.activeEraser : ''}`}
              onClick={() => setIsEraser(!isEraser)}
              title="橡皮擦"
            >
              <span className={styles.eraserIcon}>⌫</span>
            </button>
            <div className={styles.divider} />
            <div className={styles.brushSizes}>
              {[2, 5, 10, 20].map(size => (
                <button
                  key={size}
                  className={`${styles.sizeBtn} ${brushSize === size ? styles.activeSize : ''}`}
                  onClick={() => setBrushSize(size)}
                >
                  <span style={{ width: size, height: size }} />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.sidebar}>
          <div className={styles.header}>
            <h3>星云自画像</h3>
            <p>用画笔捕捉此刻的你</p>
          </div>

          <div className={styles.messageSection}>
            <label>漂流物语</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="留下一句话，随黑匣子漂流..."
              maxLength={50}
            />
            <span className={styles.charCount}>{message.length}/50</span>
          </div>

          <div className={styles.actions}>
            <button className={`${styles.actionBtn} ${styles.undoBtn}`} onClick={undo} disabled={history.length <= 1}>
              <span className={styles.icon}>↩</span>撤回
            </button>
            <button className={`${styles.actionBtn} ${styles.clearBtn}`} onClick={clearCanvas}>
              <span className={styles.icon}>✕</span>清空
            </button>
            <button className={`${styles.actionBtn} ${styles.saveBtn}`} onClick={handleSave} disabled={isLaunching}>
              <span className={styles.icon}>◇</span>封装并发射
            </button>
          </div>
        </div>

        <button className={styles.closeBtn} onClick={onClose} disabled={isLaunching}>×</button>
      </div>

      {/* 发射动画 - 全屏覆盖 */}
      {isLaunching && (
        <div className={styles.launchOverlay}>
          {/* 动态星空 */}
          <div className={styles.starsLayer}>
            {[...Array(50)].map((_, i) => (
              <div 
                key={i} 
                className={styles.star}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${2 + Math.random() * 2}s`,
                }}
              />
            ))}
          </div>
          
          {/* 扫描线效果 */}
          <div className={styles.scanLines} />
          
          {/* 主场景 */}
          <div className={styles.launchScene}>
            {/* 外圈装饰 */}
            <div className={styles.outerRing} />
            <div className={styles.outerRing2} />
            <div className={styles.outerRing3} />
            
            {/* 数据流 */}
            <div className={styles.dataStream}>
              {[...Array(8)].map((_, i) => (
                <div key={i} className={styles.dataLine} style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
            
            {/* 黑匣子 */}
            <div className={styles.blackBox}>
              <div className={styles.boxOuter}>
                <div className={styles.boxInner}>
                  {imagePreview && <img src={imagePreview} alt="" className={styles.boxImage} />}
                </div>
              </div>
              {/* 角标 */}
              <div className={styles.cornerTL} />
              <div className={styles.cornerTR} />
              <div className={styles.cornerBL} />
              <div className={styles.cornerBR} />
            </div>
            
            {/* 能量粒子 */}
            <div className={styles.particles}>
              {[...Array(12)].map((_, i) => (
                <div 
                  key={i} 
                  className={styles.particle}
                  style={{
                    '--angle': `${i * 30}deg`,
                    animationDelay: `${i * 0.1}s`,
                  }}
                />
              ))}
            </div>
            
            {/* 发射尾焰 */}
            <div className={styles.thruster}>
              <div className={styles.flame} />
              <div className={styles.flameCore} />
              <div className={styles.smokeTrail} />
            </div>
          </div>
          
          {/* 坐标信息 */}
          <div className={styles.coordDisplay}>
            <div className={styles.coordItem}>
              <span className={styles.coordLabel}>TARGET</span>
              <span className={styles.coordValue}>DEEP SPACE</span>
            </div>
            <div className={styles.coordItem}>
              <span className={styles.coordLabel}>STATUS</span>
              <span className={styles.coordValue + ' ' + styles.blink}>LAUNCHING</span>
            </div>
          </div>
          
          {/* 底部信息 */}
          <div className={styles.launchInfo}>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} />
            </div>
            <p className={styles.launchTitle}>◇ 黑匣子封装完成</p>
            <p className={styles.launchSub}>正在进入深空轨道...</p>
          </div>
        </div>
      )}
    </div>
  );
}
